import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { formatMoney } from "../lib/money";
import BookingCheckout from "./BookingCheckout";
import type { BookingQuote } from "../booking/bookingTypes";
import {
  CONTAINERS,
  INCOTERMS,
  MODES,
  SPECIAL,
  compareProviders,
  type Incoterm,
  type Mode,
  type QuoteInput,
  type QuoteOption,
  type UnitType,
} from "../lib/quoteEngine";

// ===========================================================================
// MultimodalQuoter — cotizador aéreo/marítimo (módulo A, issue #52)
// ---------------------------------------------------------------------------
// Interactive quote engine for the carga aérea/marítima module. Quotes by KG,
// by full container (FCL) or part of it (LCL) across every mode and Incoterm,
// with surcharges for special cargo (refrigerada, peligrosa, sobredimensionada,
// frágil). It compares every feasible provider/option (precio vs. tránsito) and
// shows the full cost breakdown for the chosen one. Pricing lives in the pure
// quoteEngine module; the "Reservar espacio" CTA simulates the booking + payment
// (transacción completa) that would hit the provider once integrated.
// ===========================================================================

type ModeFilter = Mode | "all";
type SortKey = "precio" | "transito";

export default function MultimodalQuoter() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [unit, setUnit] = useState<UnitType>("kg");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [weight, setWeight] = useState("500");
  const [volume, setVolume] = useState("2");
  const [container, setContainer] = useState("40gp");
  const [lclFraction, setLclFraction] = useState("0.5");
  const [incoterm, setIncoterm] = useState<Incoterm>("CIF");
  const [special, setSpecial] = useState<Record<string, boolean>>({});
  const [checkout, setCheckout] = useState<BookingQuote | null>(null);

  // Comparison controls.
  const [modeFilter, setModeFilter] = useState<ModeFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("precio");
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  // FCL/LCL is maritime-only, so a stale air/land mode filter would otherwise
  // hide every (maritime) option. Reset to "all" when leaving KG quoting.
  useEffect(() => {
    if (unit !== "kg") setModeFilter("all");
  }, [unit]);

  const inputBase =
    "w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors bg-app border-line text-content focus:border-primary-500";

  const quoteInput: QuoteInput = useMemo(
    () => ({
      // For FCL/LCL the comparison is maritime; the per-provider mode is applied
      // inside compareProviders, so the input mode only drives the KG defaults.
      mode: "maritima",
      unit,
      weight: Number(weight) || 0,
      volume: Number(volume) || 0,
      container,
      lclFraction: Number(lclFraction) || 0.5,
      incoterm,
      special: SPECIAL.filter((s) => special[s.key]).map((s) => s.key),
    }),
    [unit, weight, volume, container, lclFraction, incoterm, special],
  );

  // All feasible provider options, then filtered by mode + sorted for display.
  const options = useMemo(() => compareProviders(quoteInput), [quoteInput]);

  const visibleOptions = useMemo(() => {
    const filtered =
      modeFilter === "all" ? options : options.filter((o) => o.provider.mode === modeFilter);
    const sorted = [...filtered];
    if (sortKey === "transito") {
      sorted.sort((a, b) => a.provider.transitMinDays - b.provider.transitMinDays);
    }
    return sorted;
  }, [options, modeFilter, sortKey]);

  // Cheapest / fastest across the *visible* set, for the comparison badges.
  const cheapestId = useMemo(
    () => visibleOptions.reduce<QuoteOption | null>((best, o) => (!best || o.total < best.total ? o : best), null)?.provider.id,
    [visibleOptions],
  );
  const fastestId = useMemo(
    () =>
      visibleOptions.reduce<QuoteOption | null>(
        (best, o) => (!best || o.provider.transitMinDays < best.provider.transitMinDays ? o : best),
        null,
      )?.provider.id,
    [visibleOptions],
  );

  // The selected option falls back to the first visible (cheapest) one.
  const selected: QuoteOption | null =
    visibleOptions.find((o) => o.provider.id === selectedProviderId) ?? visibleOptions[0] ?? null;

  // compareProviders already computed the full breakdown per option; reuse it.
  const detail = selected?.breakdown ?? null;

  const handleBook = () => {
    if (!selected || !detail) return;
    // Hand the selected provider option off to the end-to-end checkout. The
    // provider name drives whether online booking is supported (quote-only
    // forwarders such as EuroFreight are blocked there with a clear message).
    setCheckout({
      origin,
      destination,
      mode: MODES.find((m) => m.value === selected.provider.mode)!.label,
      incoterm,
      basis: detail.basis,
      amount: detail.total,
      currency: "MXN",
      transitDays: selected.transitLabel,
      provider: selected.provider.name,
    });
  };

  const chip = (active: boolean) =>
    `rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
      active
        ? "border-primary-500 bg-primary-500/10 text-primary-500"
        : `${tc.cardBorder} ${tc.textSecondary} hover:border-primary-500/50`
    }`;

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
            <i className="ri-calculator-line text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>Cotizador multimodal</h1>
            <p className={`text-sm ${tc.textSecondary}`}>
              Aéreo, marítimo y terrestre internacional · KG, FCL/LCL, todos los Incoterms y carga especial
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Form */}
          <div className={`rounded-2xl border p-5 lg:col-span-2 ${tc.cardBorder} ${tc.cardBg}`}>
            {/* Route */}
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Origen</label>
                <input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="MEX / Manzanillo" className={inputBase} />
              </div>
              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Destino</label>
                <input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="MIA / Shanghai" className={inputBase} />
              </div>
            </div>

            {/* Unit type */}
            <div className="mb-4">
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Unidad de cotización</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { v: "kg", l: "Por KG" },
                  { v: "fcl", l: "Contenedor (FCL)" },
                  { v: "lcl", l: "Parte (LCL)" },
                ] as { v: UnitType; l: string }[]).map((u) => (
                  <button
                    key={u.v}
                    onClick={() => setUnit(u.v)}
                    className={`rounded-lg border px-3 py-2 text-xs transition-colors ${
                      unit === u.v
                        ? "border-primary-500 bg-primary-500/10 text-primary-500"
                        : `${tc.cardBorder} ${tc.textSecondary} hover:border-primary-500/50`
                    }`}
                  >
                    {u.l}
                  </button>
                ))}
              </div>
              {unit !== "kg" && (
                <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>
                  <i className="ri-information-line mr-1" />
                  Las cotizaciones por contenedor (FCL/LCL) se comparan entre navieras (modo marítimo).
                </p>
              )}
            </div>

            {/* Unit-specific inputs */}
            {unit === "kg" ? (
              <div className="mb-4 grid grid-cols-2 gap-3">
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Peso (kg)</label>
                  <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} className={inputBase} />
                </div>
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Volumen (m³)</label>
                  <input type="number" value={volume} onChange={(e) => setVolume(e.target.value)} className={inputBase} />
                </div>
              </div>
            ) : (
              <div className="mb-4 grid grid-cols-2 gap-3">
                <div className={unit === "fcl" ? "col-span-2" : ""}>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Tamaño de contenedor</label>
                  <select value={container} onChange={(e) => setContainer(e.target.value)} className={inputBase}>
                    {CONTAINERS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                {unit === "lcl" && (
                  <div>
                    <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Fracción usada</label>
                    <select value={lclFraction} onChange={(e) => setLclFraction(e.target.value)} className={inputBase}>
                      <option value="0.25">25%</option>
                      <option value="0.5">50%</option>
                      <option value="0.75">75%</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Incoterm */}
            <div className="mb-4">
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Incoterm</label>
              <div className="flex flex-wrap gap-2">
                {INCOTERMS.map((inc) => (
                  <button key={inc} onClick={() => setIncoterm(inc)} className={chip(incoterm === inc)}>
                    {inc}
                  </button>
                ))}
              </div>
            </div>

            {/* Special cargo */}
            <div>
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Carga especial</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {SPECIAL.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSpecial((p) => ({ ...p, [s.key]: !p[s.key] }))}
                    className={`flex items-center gap-1 rounded-lg border px-2 py-2 text-xs transition-colors ${
                      special[s.key]
                        ? "border-primary-500 bg-primary-500/10 text-primary-500"
                        : `${tc.cardBorder} ${tc.textSecondary} hover:border-primary-500/50`
                    }`}
                  >
                    <i className={s.icon} />
                    <span className="truncate">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quote result — breakdown of the selected option */}
          <div className={`flex flex-col rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
            <h3 className={`mb-1 text-sm font-bold ${tc.text}`}>Cotización estimada</h3>
            {selected && detail ? (
              <>
                <p className={`mb-1 text-xs ${tc.textSecondary}`}>
                  <i className={`${MODES.find((m) => m.value === selected.provider.mode)!.icon} mr-1`} />
                  {selected.provider.name} · {detail.basis}
                </p>

                <dl className="mt-3 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <dt className={tc.textSecondary}>Costo base</dt>
                    <dd className={tc.text}>{formatMoney(detail.baseCost)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className={tc.textSecondary}>Incoterm {incoterm} (×{detail.incFactor.toFixed(2)})</dt>
                    <dd className={tc.text}>{formatMoney(detail.afterIncoterm)}</dd>
                  </div>
                  {detail.surcharges.map((s) => (
                    <div key={s.key} className="flex justify-between">
                      <dt className={tc.textSecondary}>+ {s.label} ({Math.round(s.surcharge * 100)}%)</dt>
                      <dd className="text-amber-500">incluido</dd>
                    </div>
                  ))}
                  {detail.surchargeAmount > 0 && (
                    <div className="flex justify-between">
                      <dt className={tc.textSecondary}>Recargos carga especial</dt>
                      <dd className={tc.text}>{formatMoney(detail.surchargeAmount)}</dd>
                    </div>
                  )}
                </dl>

                <div className="my-3 border-t border-dashed border-gray-400/30" />
                <div className="flex items-end justify-between">
                  <span className={`text-xs ${tc.textSecondary}`}>Total estimado</span>
                  <span className="text-2xl font-bold text-primary-500">{formatMoney(detail.total)}</span>
                </div>
                <p className={`mt-1 text-xs ${tc.textSecondary}`}>
                  <i className="ri-time-line mr-1" />Tránsito aprox.: {selected.transitLabel}
                </p>

                <button
                  onClick={handleBook}
                  className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
                >
                  <i className="ri-shopping-cart-2-line" />
                  Reservar espacio (booking + pago)
                </button>
                <p className={`mt-2 text-center text-[11px] ${tc.textSecondary}`}>
                  Tarifa de demostración. Se conectará a la API del proveedor.
                </p>
              </>
            ) : (
              <p className={`mt-4 text-xs ${tc.textSecondary}`}>
                Ningún proveedor puede cubrir esta combinación de carga. Ajusta el modo o la carga
                especial para ver opciones.
              </p>
            )}
          </div>
        </div>

        {/* Provider / option comparison */}
        <div className={`mt-5 rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className={`text-sm font-bold ${tc.text}`}>Comparación de opciones / proveedores</h3>
              <p className={`text-xs ${tc.textSecondary}`}>
                {visibleOptions.length} opción(es) para esta carga · ordenadas por{" "}
                {sortKey === "precio" ? "precio" : "tránsito"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Mode filter (KG can span modes; FCL/LCL is maritime-only) */}
              <button onClick={() => setModeFilter("all")} className={chip(modeFilter === "all")}>
                Todos
              </button>
              {MODES.map((m) => (
                <button
                  key={m.value}
                  onClick={() => setModeFilter(m.value)}
                  disabled={unit !== "kg" && m.value !== "maritima"}
                  className={`${chip(modeFilter === m.value)} ${
                    unit !== "kg" && m.value !== "maritima" ? "cursor-not-allowed opacity-40" : ""
                  }`}
                >
                  <i className={`${m.icon} mr-1`} />
                  {m.label}
                </button>
              ))}
              <span className={`ml-1 text-xs ${tc.textSecondary}`}>Orden:</span>
              <button onClick={() => setSortKey("precio")} className={chip(sortKey === "precio")}>
                Precio
              </button>
              <button onClick={() => setSortKey("transito")} className={chip(sortKey === "transito")}>
                Tránsito
              </button>
            </div>
          </div>

          {visibleOptions.length === 0 ? (
            <p className={`py-6 text-center text-xs ${tc.textSecondary}`}>
              No hay proveedores que cubran esta carga con los filtros actuales.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className={`${tc.textSecondary}`}>
                    <th className="px-2 py-2 font-medium">Proveedor</th>
                    <th className="px-2 py-2 font-medium">Modo</th>
                    <th className="px-2 py-2 font-medium">Tránsito</th>
                    <th className="px-2 py-2 font-medium">Servicio</th>
                    <th className="px-2 py-2 text-right font-medium">Total</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {visibleOptions.map((o) => {
                    const isSelected = selected?.provider.id === o.provider.id;
                    return (
                      <tr
                        key={o.provider.id}
                        onClick={() => setSelectedProviderId(o.provider.id)}
                        className={`cursor-pointer border-t ${tc.cardBorder} ${
                          isSelected ? "bg-primary-500/5" : ""
                        }`}
                      >
                        <td className="px-2 py-2.5">
                          <div className={`flex items-center gap-2 font-medium ${tc.text}`}>
                            {o.provider.name}
                            {o.provider.id === cheapestId && (
                              <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-500">
                                Mejor precio
                              </span>
                            )}
                            {o.provider.id === fastestId && o.provider.id !== cheapestId && (
                              <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] text-sky-500">
                                Más rápido
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`px-2 py-2.5 ${tc.textSecondary}`}>
                          <i className={`${MODES.find((m) => m.value === o.provider.mode)!.icon} mr-1`} />
                          {MODES.find((m) => m.value === o.provider.mode)!.label}
                        </td>
                        <td className={`px-2 py-2.5 ${tc.textSecondary}`}>{o.transitLabel}</td>
                        <td className={`px-2 py-2.5 ${tc.textSecondary}`}>
                          <i className="ri-star-fill mr-1 text-amber-400" />
                          {o.provider.rating.toFixed(1)}
                        </td>
                        <td className={`px-2 py-2.5 text-right font-semibold ${tc.text}`}>
                          {formatMoney(o.total)}
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <span
                            className={`rounded-md border px-2 py-1 text-[11px] ${
                              isSelected
                                ? "border-primary-500 text-primary-500"
                                : `${tc.cardBorder} ${tc.textSecondary}`
                            }`}
                          >
                            {isSelected ? "Seleccionado" : "Elegir"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {checkout && (
        <BookingCheckout quote={checkout} onClose={() => setCheckout(null)} />
      )}
    </div>
  );
}
